import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useForm } from 'react-final-form';
import NewRadioButton from '../../../NewRadioButton';
import useIphoneDetection from '../../../../hooks/useIphoneDetection';
import { citiesListQuery } from '../../../../queries';
import Loader from '../../../Loader';
import NewSearchEmpty from '../NewSearchEmpty';
import {
  setCitiesList,
  showPopupHeader,
  setChosenLocationNames,
  setCurrentCity,
  setError,
  setCurrentState,
  setLocationIds,
  setResetButtonIsDisabled
} from '../../../../actions';
import { entitiesFilter } from '../../../../utils/helpers/common';
import { FORM_VALUES } from '../../../../utils/helpers/constants';

const NewSearchCity = ({
  listType,
  isFocused,
  searchString,
  cancelHandler,
  closePopupHandler
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { change, getState } = useForm();
  const { data, loading, error } = useQuery(citiesListQuery);
  const isIphone = useIphoneDetection();
  const isCreate = listType === 'create';
  const currentCity = useSelector(state => state.searchForm.currentCity);
  const storedFormState = useSelector(state => state.searchForm.currentState);

  const items = (
    isCreate
      ? []
      : [
          {
            id: '0',
            name: t('search.city_search.any'),
            slug: null,
            __typename: 'City'
          }
        ]
  ).concat((data && data.cities) || []);

  useEffect(() => {
    dispatch(setCitiesList(items));
  }, [data]);

  useEffect(() => {
    if (error) dispatch(setError(true));
  }, [error]);

  useEffect(() => {
    const citiesPopup = document.getElementsByClassName('search-popup--cities');
    const container = citiesPopup[0]?.querySelector('.search-popup__container');
    const touchHandler = () => {
      const input = citiesPopup[0].querySelector('input[type="search"]');

      input.blur();
    };

    if (isIphone) {
      container?.addEventListener('touchstart', touchHandler);
    }

    return () => {
      if (isIphone) {
        container && container.removeEventListener('touchstart', touchHandler);
      }
    };
  }, [isIphone]);

  const onChangeRadioHandler = e => {
    const cityId = e.target.value;
    change('city_id', cityId);
    if (isCreate) {
      change(FORM_VALUES.locationId, undefined);
      change(FORM_VALUES.locationName, undefined);
    } else {
      dispatch(setCurrentCity(cityId));
      dispatch(
        setCurrentState({
          ...storedFormState,
          city_id: cityId,
          location_ids: undefined
        })
      );
      change('location_ids', undefined);
      dispatch(setLocationIds([]));
      dispatch(setResetButtonIsDisabled(false));
      dispatch(setChosenLocationNames([]));
    }
    dispatch(showPopupHeader());
    closePopupHandler();
    cancelHandler();
  };

  const citiesList =
    (!isFocused || searchString) &&
    entitiesFilter(items, searchString).map(city => (
      <NewRadioButton
        key={city.id}
        data={city}
        onChangeHandler={onChangeRadioHandler}
        currentValue={getState().values.city_id || currentCity}
        name="search-city"
        cypress="city"
      />
    ));
  const emptySearch = !citiesList.length && searchString && (
    <NewSearchEmpty
      onClickHandler={cancelHandler}
      linkText={t('search.city_search.back')}
    />
  );

  if (error) {
    return null;
  }

  return (
    <div className="new-search-popup__container">
      <Loader loading={loading}>{citiesList}</Loader>
      {emptySearch}
    </div>
  );
};

NewSearchCity.propTypes = {
  isFocused: PropTypes.bool.isRequired,
  listType: PropTypes.string.isRequired,
  cancelHandler: PropTypes.func.isRequired,
  searchString: PropTypes.string.isRequired,
  closePopupHandler: PropTypes.func.isRequired
};

export default NewSearchCity;
